import { comluxgroup } from '@luxuryescapes/contract-data-event-schemas'
import { trackClientEvent } from 'analytics/analytics'
import type { SearchCalendarEvent } from 'constants/searchCalendarEvents'
import {
  countHighlightedDatesInMonth,
  eventsOverlappingStay,
  selectPrimaryEvent,
} from 'lib/search/searchCalendarEvents'

const SUBJECT = 'search_calendar_event_overlay'
const CATEGORY = 'search'
const LIST_NAME = 'search_calendar_event_overlay'

/**
 * `createListItem_1_0_0` has no free-form metadata field, so the month, event and
 * searched places travel as a `key=value|key=value` string. One encoder keeps
 * impression, hover and apply parseable the same way downstream.
 */
function encodeListInstance(fields: Record<string, string | undefined>): string {
  return Object.entries(fields)
    .map(([key, value]) => `${key}=${value || 'none'}`)
    .join('|')
}

function buildContext(listInstance: string, itemPosition: number) {
  return [
    comluxgroup.createListItem_1_0_0({
      listName: LIST_NAME,
      listInstance,
      itemPosition,
    }),
  ]
}

interface SearchCalendarEventImpressionParams {
  /** The visible calendar month, as `YYYY-MM`. */
  monthKey: string
  event: Pick<SearchCalendarEvent, 'id' | 'startDate' | 'endDate'>
  placeIds: ReadonlyArray<string>
  /** Whether the overlay was actually painted, i.e. the user is in a treatment variation. */
  isHighlighted: boolean
}

/**
 * One impression per visible month that holds at least one event date.
 *
 * Fires for the control arm too, as `impression_not_highlighted`, so the treatment
 * arm's click-through has a matched denominator to be compared against. Both actions
 * are equally "eligible" — the event only fires for a month holding event dates — so
 * the only thing that separates them is whether anything was painted.
 */
export function trackSearchCalendarEventOverlayImpression(
  params: SearchCalendarEventImpressionParams,
): void {
  trackClientEvent({
    subject: SUBJECT,
    action: params.isHighlighted ? 'impression_highlighted' : 'impression_not_highlighted',
    category: CATEGORY,
    type: 'nonInteraction',
    context: buildContext(
      encodeListInstance({
        month: params.monthKey,
        calendarEventId: params.event.id,
        placeIds: params.placeIds.join(','),
      }),
      countHighlightedDatesInMonth(params.event, params.monthKey),
    ),
  })
}

interface SearchCalendarEventHoverParams {
  calendarEventId: string
  placeIds: ReadonlyArray<string>
}

function trackHover(action: string, params: SearchCalendarEventHoverParams): void {
  trackClientEvent({
    subject: SUBJECT,
    action,
    category: CATEGORY,
    type: 'interaction',
    context: buildContext(
      encodeListInstance({
        calendarEventId: params.calendarEventId,
        placeIds: params.placeIds.join(','),
      }),
      1,
    ),
  })
}

/** Fires once each time the pointer enters a highlighted range; re-entry counts again. */
export function trackSearchCalendarEventOverlayHover(params: SearchCalendarEventHoverParams): void {
  trackHover('hover_highlighted_dates', params)
}

/**
 * Fires once each time the pointer enters a footer legend chip, which is what reveals
 * the event description. Separate from the calendar-range hover so reading the copy
 * can be told apart from grazing the dates.
 */
export function trackSearchCalendarEventOverlayLegendHover(params: SearchCalendarEventHoverParams): void {
  trackHover('hover_legend', params)
}

/**
 * Fires once each time the pointer enters a chevron hint, which reveals the event
 * name for a month one page away. Distinct from the other two hovers: this one says
 * the user engaged with a suggestion they could not yet see.
 */
export function trackSearchCalendarEventOverlayHintHover(params: SearchCalendarEventHoverParams): void {
  trackHover('hover_hint', params)
}

/**
 * Fires on apply for both arms whenever the experiment is turned on.
 *
 * The verdict is judged against every event matching the searched destination, NOT
 * against whatever the calendar happens to be scrolled to. Tying it to the visible
 * months would report a stay as missing the event whenever the user paged away
 * before applying, which silently undercounts the conversion this experiment
 * measures.
 *
 * The action says `event_overlap` rather than `suggested` deliberately. Control saw
 * no suggestion at all, and the test is a loose intersection — one shared night in a
 * fortnight's stay counts — so `suggested` would overstate it on both arms. Which
 * arm the user was in is a join on the `exp_search_calendar_event_overlay` flag.
 *
 * An overlap always counts, whatever the calendar is scrolled to — the stay either
 * spent a night during the event or it did not, and gating that on the viewport
 * would drop conversions from anyone who paged away before applying.
 *
 * A non-overlap only counts when a suggestion was surfaced to decline. Without that
 * gate the negative fires on every date apply across the whole hotel funnel, and
 * `calendarEventId=none` stops meaning "the user saw a suggestion and chose other
 * dates", which is the only reading that makes it a usable denominator. This also
 * covers destinations with no configured events.
 *
 * The two ways a suggestion can surface get their own actions, because they are very
 * different evidence: a painted range declined is a real rejection, whereas a 20px
 * chevron hint may never have been noticed. Merging them would bury a weak signal in
 * the headline denominator with no field to separate them again afterwards — and the
 * painted-versus-hinted mix shifts with how much a user pages, which the overlay
 * itself may influence.
 *
 * Both flags are computed for both arms. Control renders neither the wash nor the
 * hints, but scoping them to what is actually drawn would give treatment a larger
 * eligible population and bias the ratio between them.
 */
function applyAction(
  overlappingEvent: SearchCalendarEvent | undefined,
  hadVisibleSuggestion?: boolean,
): string {
  if (overlappingEvent) return 'dates_applied_event_overlap'

  return hadVisibleSuggestion ?
    'dates_applied_no_event_overlap' :
    'dates_applied_hint_no_event_overlap'
}

export function maybeTrackSearchCalendarEventOverlayApply(params: {
  isTurnedOn: boolean
  /** Every event matching the searched destination — decides the verdict. */
  events: ReadonlyArray<SearchCalendarEvent>
  /** A suggestion was painted in the visible months (or would have been, on control). */
  hadVisibleSuggestion?: boolean
  /** A suggestion was only hinted beside a chevron, one page either side. */
  hadHintedSuggestion?: boolean
  placeIds: ReadonlyArray<string>
  checkInIso?: string
  checkOutIso?: string
}): void {
  if (!params.isTurnedOn || !params.checkInIso || !params.checkOutIso) return

  const overlappingEvent = selectPrimaryEvent(
    eventsOverlappingStay(params.events, params.checkInIso, params.checkOutIso),
  )
  if (!overlappingEvent && !params.hadVisibleSuggestion && !params.hadHintedSuggestion) return

  trackClientEvent({
    subject: SUBJECT,
    action: applyAction(overlappingEvent, params.hadVisibleSuggestion),
    category: CATEGORY,
    type: 'interaction',
    context: buildContext(
      encodeListInstance({
        checkIn: params.checkInIso,
        checkOut: params.checkOutIso,
        calendarEventId: overlappingEvent?.id,
        placeIds: params.placeIds.join(','),
      }),
      1,
    ),
  })
}
